export const DEFAULT_MANGO_LOGO_URL = "/app-assets/svg/mango-icon.svg";
export const DEFAULT_NEUTRAL_LOADING_LOGO_URL =
	"https://placehold.co/60?text=Logo";

export interface WhitelabelBrandingConfig {
	removeBranding?: boolean | null;
	logoUrl?: string | null;
}

export interface AccountPublicOriginConfig {
	subdomain?: string | null;
	brandingConfig?: WhitelabelBrandingConfig | null;
}

export const isWhitelabelEnabled = (
	brandingConfig?: WhitelabelBrandingConfig | null,
) => Boolean(brandingConfig?.removeBranding);

const normalizeHostname = (value: string | null | undefined): string | null => {
	const containsControlCharacter = value
		? Array.from(value).some((character) => {
				const codePoint = character.codePointAt(0) ?? 0;
				return codePoint <= 31 || codePoint === 127;
			})
		: false;
	if (
		!value ||
		value !== value.trim() ||
		/\s/u.test(value) ||
		containsControlCharacter
	) {
		return null;
	}
	const hostname = value.toLowerCase();
	if (!hostname) return null;

	try {
		const url = new URL(`https://${hostname}`);
		if (
			url.hostname !== hostname ||
			url.username ||
			url.password ||
			url.port ||
			url.pathname !== "/" ||
			url.search ||
			url.hash
		) {
			return null;
		}
		return hostname;
	} catch {
		return null;
	}
};

export const resolveAccountPublicHost = (
	account: AccountPublicOriginConfig,
	configuredRouterDomain: string,
): string => {
	const routerDomain = normalizeHostname(configuredRouterDomain);
	if (!routerDomain) throw new Error("ROUTER_DOMAIN_NAME is not configured");

	const customDomain = normalizeHostname(account.subdomain);
	return isWhitelabelEnabled(account.brandingConfig) && customDomain
		? customDomain
		: routerDomain;
};

export const resolveAccountPublicOrigin = (
	account: AccountPublicOriginConfig,
	routerDomain: string,
): string => `https://${resolveAccountPublicHost(account, routerDomain)}`;

export const buildAccountPublicUrl = (
	account: AccountPublicOriginConfig,
	routerDomain: string,
	path: string,
): string => {
	if (!path.startsWith("/") || path.startsWith("//")) {
		throw new Error(
			"Account public URL path must be an internal absolute path",
		);
	}
	return new URL(
		path,
		resolveAccountPublicOrigin(account, routerDomain),
	).toString();
};

export const resolvePortalLogoUrl = (
	brandingConfig?: WhitelabelBrandingConfig | null,
): string | null => {
	const customLogoUrl = brandingConfig?.logoUrl?.trim();

	if (customLogoUrl) return customLogoUrl;
	if (isWhitelabelEnabled(brandingConfig)) return null;
	return DEFAULT_MANGO_LOGO_URL;
};

export const resolveLoadingLogoUrl = (
	brandingConfig?: WhitelabelBrandingConfig | null,
) => {
	const customLogoUrl = brandingConfig?.logoUrl?.trim();

	if (isWhitelabelEnabled(brandingConfig))
		return customLogoUrl ?? DEFAULT_NEUTRAL_LOADING_LOGO_URL;
	return DEFAULT_MANGO_LOGO_URL;
};
